import {
  ApplicationQuestion,
  CommonQuestionKeys,
  FieldType,
  SMBCapitalCustomQuestionKeys,
  TextInputType,
} from '@bybeam/platform-types';
import { YesNoOptions } from '@platform-ui/application/utils/index.js';
import usaStates from '@platform-ui/common/utilities/usaStates.json';
import { and, eq, inList, or } from '../../../ast/utils.js';
import {
  ASSET_TYPES,
  BENEFITS,
  BUSINESS_REASONS,
  CAPITAL_RAISING_EFFORTS,
  CERTIFICATIONS,
  CITY_DEPARTMENTS,
  COVID_RELIEF_PROGRAMS,
  DEBT_LEVELS,
  DEVICES,
  EDUCATION_LEVELS,
  ETHNICITIES,
  FUNDING_CHALLENGES,
  INCOME_LEVELS,
  INDUSTRIES,
  LANGUAGES,
  LEGAL_STRUCTURES,
  MARITAL_STATUSES,
  MARKETING_MATERIALS,
  PERCENTAGE_RANGES,
  POS_SYSTEMS,
  RESOURCES,
  REVENUE_RANGES,
  ROLES,
  SECTORS,
  SOCIAL_MEDIA_PLATFORMS,
  TECH_TOPICS,
  TRANSPORTATION_MODES,
  WORKSPACES,
} from '../options.js';

type ExtractedCommonQuestionKeys = Extract<
  CommonQuestionKeys,
  | CommonQuestionKeys.GENDER
  | CommonQuestionKeys.ETHNICITY
  | CommonQuestionKeys.LANGUAGE
  | CommonQuestionKeys.HOUSING_TYPE
  | CommonQuestionKeys.ADDRESS
  | CommonQuestionKeys.VETERAN
  | CommonQuestionKeys.DISABLED
  | CommonQuestionKeys.MARITAL_STATUS
  | CommonQuestionKeys.HOUSEHOLD_INCOME
  | CommonQuestionKeys.AFFIRMATION
>;

export const QUESTIONS: {
  [key in ExtractedCommonQuestionKeys | SMBCapitalCustomQuestionKeys]: ApplicationQuestion;
} = {
  // PERSONAL INFORMATION
  [CommonQuestionKeys.GENDER]: {
    key: CommonQuestionKeys.GENDER,
    copy: { title: 'Which option best describes your gender identity?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Gender',
        key: 'gender.gender',
        options: [
          { value: 'female', label: 'Female' },
          { value: 'male', label: 'Male' },
          { value: 'prefer-not-to-answer', label: 'Prefer not to say' },
          { value: 'other', label: 'Non-Binary / Other' },
        ],
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'gender.otherGender',
        dynamicLogic: inList('gender.gender', 'other'),
        copy: 'Other',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.BIRTH_YEAR]: {
    key: SMBCapitalCustomQuestionKeys.BIRTH_YEAR,
    copy: { title: 'In what year were you born?' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Birth year',
        key: SMBCapitalCustomQuestionKeys.BIRTH_YEAR,
        props: {
          inputProps: {
            maxlength: 4,
          },
          helperText: 'Please use YYYY format (e.g., 1990)',
        },
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.BORN_IN_US]: {
    key: SMBCapitalCustomQuestionKeys.BORN_IN_US,
    copy: { title: 'Were you born in the United States?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'bornInUS.bornInUS',
        options: YesNoOptions,
      },
      {
        type: FieldType.Dropdown,
        copy: 'What state were you born in?',
        options: usaStates.map((s) => ({ label: s.name, value: s.abbreviation })),
        key: 'bornInUS.whatState',
        dynamicLogic: eq('bornInUS.bornInUS', 'yes'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'What country were you born in?',
        key: 'bornInUS.whatCountry',
        dynamicLogic: eq('bornInUS.bornInUS', 'no'),
      },
    ],
  },
  [CommonQuestionKeys.ETHNICITY]: {
    key: CommonQuestionKeys.ETHNICITY,
    copy: { title: 'Do you identify as any of the following?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Ethnicity',
        key: 'ethnicity.ethnicity',
        props: { multiple: true },
        options: ETHNICITIES,
      },
      {
        type: FieldType.Typography,
        key: 'ethnicity.other.label',
        copy: 'If you identify with an ethnicity not included in the previous question, please list it here.',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('ethnicity.ethnicity', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'ethnicity.other',
        dynamicLogic: inList('ethnicity.ethnicity', 'other'),
        copy: 'Other ethnicity',
      },
    ],
  },
  [CommonQuestionKeys.LANGUAGE]: {
    key: CommonQuestionKeys.LANGUAGE,
    copy: { title: 'What is the primary language spoken in your household?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Language',
        key: 'language.language',
        options: LANGUAGES,
      },
      {
        type: FieldType.Typography,
        key: 'language.other.label',
        copy: 'If you primarily speak a language not included, please list it here:',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('language.language', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'language.other',
        dynamicLogic: inList('language.language', 'other'),
        copy: 'Other language',
      },
    ],
  },
  [CommonQuestionKeys.HOUSING_TYPE]: {
    key: CommonQuestionKeys.HOUSING_TYPE,
    copy: { title: 'Do you own a home or rent?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: CommonQuestionKeys.HOUSING_TYPE,
        options: [
          { label: 'Own a home', value: 'ownAHome' },
          { label: 'Rent or lease a house', value: 'rentOrLeaseHouse' },
          { label: 'Rent or lease a condo/apartment', value: 'rentOrLeaseCondoOrApartment' },
          { label: 'Houseless', value: 'houseless' },
        ],
      },
    ],
  },
  [CommonQuestionKeys.ADDRESS]: {
    key: CommonQuestionKeys.ADDRESS,
    copy: { title: 'What is your address?' },
    fields: [
      {
        type: FieldType.Address,
        copy: 'Address',
        key: CommonQuestionKeys.ADDRESS,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.BUSINESS_HOURS]: {
    key: SMBCapitalCustomQuestionKeys.BUSINESS_HOURS,
    copy: { title: 'Do you run your business Full-Time or Part-Time?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.BUSINESS_HOURS,
        options: [
          { label: 'Full-Time (30 hours per week or more)', value: 'fullTime' },
          { label: 'Part-Time (Less than 30 hours per week)', value: 'partTime' },
        ],
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.OTHER_EMPLOYMENT]: {
    key: SMBCapitalCustomQuestionKeys.OTHER_EMPLOYMENT,
    copy: { title: 'Are you employed at a job other than your own business?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.OTHER_EMPLOYMENT,
        options: [
          { label: 'Yes, Full Time (30 hours per week or more)', value: 'yesFullTime' },
          { label: 'Yes, Part Time (Less than 30 hours per week)', value: 'yesPartTime' },
          { label: 'No', value: 'no' },
        ],
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.ACADEMIC_STATUS]: {
    key: SMBCapitalCustomQuestionKeys.ACADEMIC_STATUS,
    copy: { title: 'Are you a student or studying for a degree?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.ACADEMIC_STATUS,
        options: [
          { label: 'Yes, Full Time', value: 'yesFullTime' },
          { label: 'Yes, Part Time', value: 'yesPartTime' },
          { label: 'No', value: 'no' },
        ],
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.SECTOR]: {
    key: SMBCapitalCustomQuestionKeys.SECTOR,
    copy: { title: 'In which sector or industry are you currently employed?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Sector or industry',
        key: 'sector.sector',
        options: SECTORS,
        props: { multiple: true },
      },
      {
        type: FieldType.Typography,
        key: 'sector.other.label',
        copy: 'If you are employed in a sector not included, please list it here:',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('sector.sector', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'sector.other',
        dynamicLogic: inList('sector.sector', 'other'),
        copy: 'Other sector',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.EDUCATION_LEVEL]: {
    key: SMBCapitalCustomQuestionKeys.EDUCATION_LEVEL,
    copy: { title: 'What is the highest level of education you have completed?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Education level',
        key: 'educationLevel.level',
        options: EDUCATION_LEVELS,
      },
      {
        type: FieldType.Typography,
        key: 'educationLevel.other.label',
        copy: 'If your education level is not included, please list it here:',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('educationLevel.level', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'educationLevel.other',
        dynamicLogic: inList('educationLevel.level', 'other'),
        copy: 'Education level',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.STUDENT_DEBT]: {
    key: SMBCapitalCustomQuestionKeys.STUDENT_DEBT,
    copy: { title: 'Please estimate the amount of student debt you have outstanding.' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Student debt',
        key: 'studentDebt.level',
        options: DEBT_LEVELS,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.PANDEMIC_DEBT_PAYMENTS]: {
    key: SMBCapitalCustomQuestionKeys.PANDEMIC_DEBT_PAYMENTS,
    copy: {
      title: 'Have you continued to make payments on your student loans through the pandemic?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.PANDEMIC_DEBT_PAYMENTS,
        options: [
          {
            label: 'Yes',
            value: 'yes',
          },
          {
            label: 'No',
            value: 'no',
          },
          {
            label: 'Not Applicable',
            value: 'n/a',
          },
        ],
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.CRIMINAL_HISTORY_OR_FELONY]: {
    key: SMBCapitalCustomQuestionKeys.CRIMINAL_HISTORY_OR_FELONY,
    copy: { title: 'Do you have any criminal history or felony convictions?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.CRIMINAL_HISTORY_OR_FELONY,
        options: YesNoOptions,
      },
    ],
  },
  [CommonQuestionKeys.VETERAN]: {
    key: CommonQuestionKeys.VETERAN,
    copy: { title: 'Are you a veteran of the U.S. Armed Forces?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: CommonQuestionKeys.VETERAN,
        options: YesNoOptions,
      },
    ],
  },
  [CommonQuestionKeys.DISABLED]: {
    key: CommonQuestionKeys.DISABLED,
    copy: { title: 'Do you have a disability of any kind?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: CommonQuestionKeys.DISABLED,
        options: YesNoOptions,
      },
    ],
  },
  [CommonQuestionKeys.MARITAL_STATUS]: {
    key: CommonQuestionKeys.MARITAL_STATUS,
    copy: { title: 'Which option best describes your marital status?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Marital status',
        key: 'maritalStatus.status',
        options: MARITAL_STATUSES,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.DEPENDENTS]: {
    key: SMBCapitalCustomQuestionKeys.DEPENDENTS,
    copy: { title: 'Do You Have Any Dependents?' },
    fields: [
      {
        type: FieldType.Typography,
        key: 'dependents.dependents.label',
        copy: 'Select All that apply',
        props: { variant: 'body', alignment: 'center' },
      },
      {
        type: FieldType.Dropdown,
        copy: 'Dependents',
        key: 'dependents.dependents',
        options: [
          { label: 'Yes, I have dependents under the age of 18', value: 'yesUnder18' },
          { label: 'No, I do not have dependents under the age of 18', value: 'no' },
          { label: 'Yes, I have dependents 18 years of age or older', value: 'yesOver18' },
        ],
        props: { multiple: true },
      },
      {
        type: FieldType.Typography,
        key: 'dependents.yesOver18.number.label',
        copy: 'How many dependents do you have that are 18 or older?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('dependents.dependents', 'yesOver18'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        key: 'dependents.yesOver18.number',
        copy: 'Number of dependents',
        dynamicLogic: inList('dependents.dependents', 'yesOver18'),
      },
      {
        type: FieldType.Typography,
        key: 'dependents.yesUnder18.number.label',
        copy: 'How many dependents under the age of 18 do you have?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('dependents.dependents', 'yesUnder18'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        key: 'dependents.yesUnder18.number',
        copy: 'Number of dependents',
        dynamicLogic: inList('dependents.dependents', 'yesUnder18'),
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.ISSUES_WITH_AFFORDABLE_CHILDCARE]: {
    key: SMBCapitalCustomQuestionKeys.ISSUES_WITH_AFFORDABLE_CHILDCARE,
    copy: {
      title:
        'Have you experienced issues with access to affordable childcare services since the pandemic began?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        options: [
          {
            label: 'Yes',
            value: 'yes',
          },
          {
            label: 'No',
            value: 'no',
          },
          {
            label: 'Not Applicable',
            value: 'n/a',
          },
        ],
        key: SMBCapitalCustomQuestionKeys.ISSUES_WITH_AFFORDABLE_CHILDCARE,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.TRANSPORTATION]: {
    key: SMBCapitalCustomQuestionKeys.TRANSPORTATION,
    copy: { title: 'If you commute to work, what modes of transportation do you use?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Transportation modes',
        key: 'transportation.transportation',
        options: TRANSPORTATION_MODES,
        props: { multiple: true },
      },
    ],
  },
  [CommonQuestionKeys.HOUSEHOLD_INCOME]: {
    key: CommonQuestionKeys.HOUSEHOLD_INCOME,
    copy: { title: 'What is your Average Annual Household Income?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Average Annual Income',
        key: 'householdIncome.annualAmount',
        options: INCOME_LEVELS,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.HAS_BANK_ACCOUNT]: {
    key: SMBCapitalCustomQuestionKeys.HAS_BANK_ACCOUNT,
    copy: { title: 'Do you have a personal bank account?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.HAS_BANK_ACCOUNT,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.OWNS_ASSETS]: {
    key: SMBCapitalCustomQuestionKeys.OWNS_ASSETS,
    copy: { title: 'Do you own any of the following assets?' },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'ownsAssets.assets',
        options: ASSET_TYPES,
        copy: 'Assets',
        props: { multiple: true },
      },
      {
        type: FieldType.Typography,
        key: 'ownsAssets.other.label',
        copy: 'Please describe any other assets you hold below.',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('ownsAssets.assets', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'ownsAssets.other',
        dynamicLogic: inList('ownsAssets.assets', 'other'),
        copy: 'Other assets',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.CREDIT_SCORE_ASSISTANCE]: {
    key: SMBCapitalCustomQuestionKeys.CREDIT_SCORE_ASSISTANCE,
    copy: { title: 'Are you getting assistance to improve or raise your personal credit score?' },
    fields: [
      {
        type: FieldType.RadioList,
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
          { label: 'Interested', value: 'interested' },
        ],
        key: SMBCapitalCustomQuestionKeys.CREDIT_SCORE_ASSISTANCE,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.BUSINESS_REASON]: {
    key: SMBCapitalCustomQuestionKeys.BUSINESS_REASON,
    copy: { title: 'Why did you start your business?' },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'businessReason.reason',
        options: BUSINESS_REASONS,
        copy: 'Reason',
        props: { multiple: true },
      },
      {
        type: FieldType.Typography,
        key: 'businessReason.other.label',
        copy: 'Open ended- describe your why?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('businessReason.reason', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 200 },
        },
        key: 'businessReason.other',
        dynamicLogic: inList('businessReason.reason', 'other'),
        copy: 'Other reason',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.NAME_OF_BUSINESS]: {
    key: SMBCapitalCustomQuestionKeys.NAME_OF_BUSINESS,
    copy: {
      title: 'Name of Business:',
    },
    fields: [
      {
        type: FieldType.Text,
        key: SMBCapitalCustomQuestionKeys.NAME_OF_BUSINESS,
        inputType: TextInputType.Text,
        copy: 'Name of Business:',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.DOING_BUSINESS_AS]: {
    key: SMBCapitalCustomQuestionKeys.DOING_BUSINESS_AS,
    copy: {
      title: 'Doing Business As:',
    },
    fields: [
      {
        type: FieldType.Text,
        key: `${SMBCapitalCustomQuestionKeys.DOING_BUSINESS_AS}.name`,
        inputType: TextInputType.Text,
        copy: 'Doing Business As:',
        validation: { required: false },
      },
    ],
    skippable: true,
  },
  [SMBCapitalCustomQuestionKeys.LEGAL_STRUCTURE]: {
    key: SMBCapitalCustomQuestionKeys.LEGAL_STRUCTURE,
    copy: { title: 'How is your business legally structured?' },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'legalStructure.structure',
        options: LEGAL_STRUCTURES,
        copy: 'Structure',
      },
      {
        type: FieldType.Typography,
        key: 'legalStructure.other.label',
        copy: 'If your legal structure is not included, please list it here:',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('legalStructure.structure', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'legalStructure.other',
        dynamicLogic: inList('legalStructure.structure', 'other'),
        copy: 'Other structure',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.OPERATING_AGREEMENT]: {
    key: SMBCapitalCustomQuestionKeys.OPERATING_AGREEMENT,
    copy: { title: 'Does your business have an Operating Agreement?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.OPERATING_AGREEMENT,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.INSURED]: {
    key: SMBCapitalCustomQuestionKeys.INSURED,
    copy: { title: 'Do you have insurance for your business?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.INSURED,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.INDUSTRY]: {
    key: SMBCapitalCustomQuestionKeys.INDUSTRY,
    copy: { title: 'What industry best describes your business?' },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'industry.industry',
        options: INDUSTRIES,
        copy: 'Industries',
        props: { multiple: true },
      },
      {
        type: FieldType.Typography,
        key: 'industry.other.label',
        copy: 'If your industry is not included, please describe it here:',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('industry.industry', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'industry.other',
        dynamicLogic: inList('industry.industry', 'other'),
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 200 },
        },
        copy: 'Other industry description',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.SOCIAL_IMPACT_VENTURE]: {
    key: SMBCapitalCustomQuestionKeys.SOCIAL_IMPACT_VENTURE,
    copy: { title: 'Do you consider your business to be a Social Impact venture?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.SOCIAL_IMPACT_VENTURE,
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
          { label: "Not sure / I don't know what this is.", value: 'notSureOrIDontKnowWhatThisIs' },
        ],
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.BRANDING_MATERIALS]: {
    key: SMBCapitalCustomQuestionKeys.BRANDING_MATERIALS,
    copy: { title: 'What branding materials do you have for your business?' },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'brandingMaterials.material',
        options: MARKETING_MATERIALS,
        copy: 'Branding Materials',
        props: { multiple: true },
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.BRANDING_MATERIALS_INTERESTED_IN]: {
    key: SMBCapitalCustomQuestionKeys.BRANDING_MATERIALS_INTERESTED_IN,
    copy: { title: 'What branding materials are you most interested in developing?' },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'brandingMaterialsInterestedIn.interest',
        options: MARKETING_MATERIALS,
        copy: 'Branding materials interested in',
        props: { multiple: true },
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.SOCIAL_MEDIA]: {
    key: SMBCapitalCustomQuestionKeys.SOCIAL_MEDIA,
    copy: { title: 'What social media channels do you use to promote your business?' },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'socialMedia.media',
        options: SOCIAL_MEDIA_PLATFORMS,
        copy: 'Social media channels',
        props: { multiple: true },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'socialMedia.facebook.link',
        dynamicLogic: inList('socialMedia.media', 'facebook'),
        copy: 'Link to Facebook',
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'socialMedia.instagram.link',
        dynamicLogic: inList('socialMedia.media', 'instagram'),
        copy: 'Link to Instagram',
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'socialMedia.xFormerlyTwitter.link',
        dynamicLogic: inList('socialMedia.media', 'xFormerlyTwitter'),
        copy: 'Link to X (Formerly Twitter)',
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'socialMedia.linkedIn.link',
        dynamicLogic: inList('socialMedia.media', 'linkedIn'),
        copy: 'Link to LinkedIn',
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'socialMedia.threads.link',
        dynamicLogic: inList('socialMedia.media', 'threads'),
        copy: 'Link to Threads',
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'socialMedia.other.link',
        dynamicLogic: inList('socialMedia.media', 'other'),
        copy: 'Link to other platforms',
        validation: { required: false },
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.RESOURCES_NEEDED]: {
    key: SMBCapitalCustomQuestionKeys.RESOURCES_NEEDED,
    copy: { title: 'What type of business development resources do you need for your business?' },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'resourcesNeeded.resource',
        options: RESOURCES,
        copy: 'Resources',
        props: { multiple: true },
      },
      {
        type: FieldType.Typography,
        key: 'resourcesNeeded.other.label',
        copy: 'If your business needs another resource not included, please describe it here:',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('resourcesNeeded.resource', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'resourcesNeeded.other',
        dynamicLogic: inList('resourcesNeeded.resource', 'other'),
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 200 },
        },
        copy: 'Other resource',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.REVENUE_FROM_ONLINE_SALES]: {
    key: SMBCapitalCustomQuestionKeys.REVENUE_FROM_ONLINE_SALES,
    copy: { title: 'How much of your revenue is generated from online sales?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.REVENUE_FROM_ONLINE_SALES,
        options: PERCENTAGE_RANGES,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.ONLINE_AD_BUDGET]: {
    key: SMBCapitalCustomQuestionKeys.ONLINE_AD_BUDGET,
    copy: { title: 'How much do you spend monthly to advertise online?' },
    fields: [
      {
        type: FieldType.Text,
        key: SMBCapitalCustomQuestionKeys.ONLINE_AD_BUDGET,
        inputType: TextInputType.Currency,
        copy: 'Please enter the whole dollar amount.',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.REVENUE_FROM_POP_UPS]: {
    key: SMBCapitalCustomQuestionKeys.REVENUE_FROM_POP_UPS,
    copy: { title: 'Do you generate revenue from flash retailing/pop up sales?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.REVENUE_FROM_POP_UPS,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.AVERAGE_ANNUAL_REVENUE_RANGE]: {
    key: SMBCapitalCustomQuestionKeys.AVERAGE_ANNUAL_REVENUE_RANGE,
    copy: { title: 'What is the average annual revenue or sales for your business?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Average annual revenue',
        key: SMBCapitalCustomQuestionKeys.AVERAGE_ANNUAL_REVENUE_RANGE,
        options: REVENUE_RANGES,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.OPERATING_IN_KCMO]: {
    key: SMBCapitalCustomQuestionKeys.OPERATING_IN_KCMO,
    copy: { title: 'Is your business located or operating in Kansas City, Missouri?' },
    fields: [
      {
        type: FieldType.RadioList,
        options: [
          {
            label: 'Yes, and I am operating with a KCMO business license',
            value: 'yesOperatingWithKCMO',
          },
          {
            label: 'Yes, and I am in the process of obtaining a KCMO business license',
            value: 'yesInProcessOfObtainingKCMOLicense',
          },
          {
            label: 'Yes, but I do not have a KCMO business license',
            value: 'yesButNoLicense',
          },
          {
            label: 'No, my business is not located or operating in Kansas City, MO',
            value: 'noNotLocatedOrOperatingInKCMO',
          },
          {
            label:
              'No, my business is not located or operating in Kansas City, MO but I plan on relocating',
            value: 'noPlanOnRelocating',
          },
        ],
        key: SMBCapitalCustomQuestionKeys.OPERATING_IN_KCMO,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.INTERACTED_WITH_CITY_DEPARTMENTS]: {
    key: SMBCapitalCustomQuestionKeys.INTERACTED_WITH_CITY_DEPARTMENTS,
    copy: {
      title:
        'Did you interact with any of the following City departments while obtaining a business license?',
    },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'interactedWithCityDepartments.department',
        options: CITY_DEPARTMENTS,
        copy: 'City departments',
        props: { multiple: true },
      },
      {
        type: FieldType.Typography,
        key: 'resourcesNeeded.other.label',
        copy: 'If you interacted with another department not included, please add it here:',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('interactedWithCityDepartments.department', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'resourcesNeeded.other',
        dynamicLogic: inList('interactedWithCityDepartments.department', 'other'),
        copy: 'Other department',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.EXPERIENCE_OBTAINING_A_BUSINESS_LICENSE]: {
    key: SMBCapitalCustomQuestionKeys.EXPERIENCE_OBTAINING_A_BUSINESS_LICENSE,
    copy: {
      title:
        'Overall, how would you rate your experience obtaining a business license from the City?',
    },
    fields: [
      {
        key: 'experienceObtainingABusinessLicense.experience',
        type: FieldType.RadioList,
        options: [
          { label: 'Poor', value: 'poor' },
          { label: 'Below Average', value: 'belowAverage' },
          { label: 'Neutral / Indifferent', value: 'neutralIndifferent' },
          { label: 'Above Average', value: 'aboveAverage' },
          { label: 'Great', value: 'great' },
        ],
      },
      {
        key: 'experienceObtainingABusinessLicense.comments.label',
        type: FieldType.Typography,
        copy: 'Please provide any comments you wish to share about your experience obtaining a business license.',
        props: { variant: 'body', alignment: 'center' },
      },
      {
        key: 'experienceObtainingABusinessLicense.comments',
        type: FieldType.Text,
        copy: 'Comments (Optional)',
        inputType: TextInputType.Text,
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 200 },
        },
        validation: { required: false },
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.HAS_BUSINESS_ACCOUNT]: {
    key: SMBCapitalCustomQuestionKeys.HAS_BUSINESS_ACCOUNT,
    copy: { title: 'Do you have a business bank account?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.HAS_BUSINESS_ACCOUNT,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.HAS_RELATIONSHIP_WITH_A_BANKER]: {
    key: SMBCapitalCustomQuestionKeys.HAS_RELATIONSHIP_WITH_A_BANKER,
    copy: { title: 'Do you have an established relationship with a banker?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.HAS_RELATIONSHIP_WITH_A_BANKER,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.USES_AN_ACCOUNTANT]: {
    key: SMBCapitalCustomQuestionKeys.USES_AN_ACCOUNTANT,
    copy: { title: "Do you use an accountant to handle your business' bookkeeping?" },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.USES_AN_ACCOUNTANT,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.HAS_BUSINESS_CREDIT_CARD]: {
    key: SMBCapitalCustomQuestionKeys.HAS_BUSINESS_CREDIT_CARD,
    copy: { title: 'Do you have a business credit card for your business?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.HAS_BUSINESS_CREDIT_CARD,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.HAS_APPLIED_FOR_BUSINESS_LOANS]: {
    key: SMBCapitalCustomQuestionKeys.HAS_APPLIED_FOR_BUSINESS_LOANS,
    copy: { title: 'Have you ever applied for any loans or capital for your business?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'hasAppliedForBusinessLoans.value',
        options: YesNoOptions,
      },
      {
        type: FieldType.Typography,
        key: 'hasAppliedForBusinessLoans.type.label',
        copy: 'Please describe what type of loan here:',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: eq('hasAppliedForBusinessLoans.value', 'yes'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'hasAppliedForBusinessLoans.type',
        dynamicLogic: eq('hasAppliedForBusinessLoans.value', 'yes'),
        copy: 'Type of loan',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.CHALLENGES_FACED_ACCESSING_CAPITAL]: {
    key: SMBCapitalCustomQuestionKeys.CHALLENGES_FACED_ACCESSING_CAPITAL,
    copy: {
      title: 'What is the biggest challenge you faced when attempting to access capital?',
    },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'challengesFacedAccessingCapital.challenge',
        options: FUNDING_CHALLENGES,
        copy: 'Challenges',
        props: { multiple: true },
      },
      {
        type: FieldType.Typography,
        key: 'challengesFacedAccessingCapital.other.label',
        copy: 'If you faced another challenge not included, please add it here:',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('challengesFacedAccessingCapital.challenge', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'challengesFacedAccessingCapital.other',
        dynamicLogic: inList('challengesFacedAccessingCapital.challenge', 'other'),
        copy: 'Other challenge',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.COVID_RELIEF_PROGRAMS]: {
    key: SMBCapitalCustomQuestionKeys.COVID_RELIEF_PROGRAMS,
    copy: {
      title: 'Were you approved for any of the following COVID-19 relief funding programs?',
    },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'covidReliefPrograms.program',
        options: COVID_RELIEF_PROGRAMS,
        copy: 'Programs',
        props: { multiple: true },
      },
      {
        type: FieldType.Typography,
        key: 'covidReliefPrograms.other.label',
        copy: 'If you were approved for another COVID-19 relief funding program not included, please describe it here:',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('covidReliefPrograms.program', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'covidReliefPrograms.other',
        dynamicLogic: inList('covidReliefPrograms.program', 'other'),
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 200 },
        },
        copy: 'Other program',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.RAISING_CAPITAL]: {
    key: SMBCapitalCustomQuestionKeys.RAISING_CAPITAL,
    copy: {
      title:
        'Are you making efforts to raise capital for your business via any of the following methods?',
    },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'rasingCapital.efforts',
        options: CAPITAL_RAISING_EFFORTS,
        copy: 'Efforts',
        props: { multiple: true },
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.POS_SYSTEM]: {
    key: SMBCapitalCustomQuestionKeys.POS_SYSTEM,
    copy: {
      title: 'What POS (Point-Of-Sale) system does your business use?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.POS_SYSTEM,
        options: POS_SYSTEMS,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.BUSINESS_ADDRESS]: {
    key: SMBCapitalCustomQuestionKeys.BUSINESS_ADDRESS,
    copy: { title: 'What is the address for the location/space you are currently operating from?' },
    fields: [
      {
        type: FieldType.Address,
        copy: 'Address',
        key: SMBCapitalCustomQuestionKeys.BUSINESS_ADDRESS,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.LOCATION_CATEGORY]: {
    key: SMBCapitalCustomQuestionKeys.LOCATION_CATEGORY,
    copy: {
      title: 'Which option best describes the location/space where you operate your business?',
    },
    fields: [
      {
        type: FieldType.Dropdown,
        key: 'locationCategory.category',
        options: WORKSPACES,
        copy: 'Business Location',
      },
      {
        type: FieldType.Typography,
        key: 'locationCategory.other.label',
        copy: 'Please describe your business location here:',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: eq('locationCategory.category', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'locationCategory.other',
        dynamicLogic: eq('locationCategory.category', 'other'),
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 200 },
        },
        copy: 'Other location',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.SQUARE_FOOTAGE]: {
    key: SMBCapitalCustomQuestionKeys.SQUARE_FOOTAGE,
    copy: {
      title: 'Do you know approximately how large the space you are currently operating from is?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'squareFootage.squareFootage',
        options: [
          {
            label: 'Yes',
            value: 'yes',
          },
          {
            label: 'Not Sure',
            value: 'no',
          },
        ],
      },
      {
        type: FieldType.Typography,
        key: 'squareFootage.footage.label',
        copy: 'How large is the space?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: eq('squareFootage.squareFootage', 'yes'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        key: 'squareFootage.footage',
        copy: 'In Square Feet',
        dynamicLogic: eq('squareFootage.squareFootage', 'yes'),
      },
      {
        type: FieldType.Typography,
        key: 'squareFootage.notSure.label',
        copy: 'Please describe your space:',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: eq('squareFootage.squareFootage', 'no'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'squareFootage.notSure',
        dynamicLogic: eq('squareFootage.squareFootage', 'no'),
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 200 },
        },
        copy: 'Describe your space',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.STARTED_AT_HOME]: {
    key: SMBCapitalCustomQuestionKeys.STARTED_AT_HOME,
    copy: { title: 'Did you first start your business from your home?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.STARTED_AT_HOME,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.PLAN_TO_MOVE_TO_LARGER_SPACE]: {
    key: SMBCapitalCustomQuestionKeys.PLAN_TO_MOVE_TO_LARGER_SPACE,
    copy: { title: 'Do you plan to move or grow into a larger space?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'planToMoveToLargerSpace.plan',
        options: [
          { label: 'Yes - I plan to move this year', value: 'planToMoveThisYear' },
          { label: 'Yes - I plan to move within the next 2 years', value: 'planToMoveNext2Years' },
          { label: 'Yes - I plan to move within the next 3 years', value: 'planToMoveNext3Years' },
          {
            label: 'No - I’m comfortable in my current space for the foreseeable future',
            value: 'no',
          },
        ],
      },
      {
        type: FieldType.Typography,
        key: 'planToMoveToLargerSpace.squareFootage.label',
        copy: 'Approximately how much space do you think you will need to move into?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: or([
          eq('planToMoveToLargerSpace.plan', 'planToMoveThisYear'),
          eq('planToMoveToLargerSpace.plan', 'planToMoveNext2Years'),
          eq('planToMoveToLargerSpace.plan', 'planToMoveNext3Years'),
        ]),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        key: 'planToMoveToLargerSpace.squareFootage',
        copy: 'In Square Feet',
        dynamicLogic: or([
          eq('planToMoveToLargerSpace.plan', 'planToMoveThisYear'),
          eq('planToMoveToLargerSpace.plan', 'planToMoveNext2Years'),
          eq('planToMoveToLargerSpace.plan', 'planToMoveNext3Years'),
        ]),
      },
      {
        type: FieldType.Typography,
        key: 'planToMoveToLargerSpace.requirements.label',
        copy: 'Are there any special requirements you would need in the new space?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: or([
          eq('planToMoveToLargerSpace.plan', 'planToMoveThisYear'),
          eq('planToMoveToLargerSpace.plan', 'planToMoveNext2Years'),
          eq('planToMoveToLargerSpace.plan', 'planToMoveNext3Years'),
        ]),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'planToMoveToLargerSpace.requirements',
        dynamicLogic: or([
          eq('planToMoveToLargerSpace.plan', 'planToMoveThisYear'),
          eq('planToMoveToLargerSpace.plan', 'planToMoveNext2Years'),
          eq('planToMoveToLargerSpace.plan', 'planToMoveNext3Years'),
        ]),
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 200 },
        },
        validation: { required: false },
        copy: 'Describe your requirements (optional)',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.PAY_YOURSELF_AS_W2]: {
    key: SMBCapitalCustomQuestionKeys.PAY_YOURSELF_AS_W2,
    copy: { title: 'Are you an employee of your own business? (Do you pay yourself?)' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.PAY_YOURSELF_AS_W2,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.HAS_EMPLOYEES]: {
    key: SMBCapitalCustomQuestionKeys.HAS_EMPLOYEES,
    copy: { title: 'Do you have employees for your business?' },
    fields: [
      {
        type: FieldType.CheckboxGroup,
        options: [
          { label: 'Yes, I have Full Time employees', value: 'fullTimeEmployees' },
          { label: 'Yes, I have Part Time employees', value: 'partTimeEmployees' },
          { label: 'No - No employees', value: 'noEmployees' },
          { label: 'No - Just contractors/consultants (1099)', value: 'contractorsConsultants' },
        ],
        key: 'hasEmployees.values',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.NUMBER_OF_FULLTIME_EMPLOYEES]: {
    key: SMBCapitalCustomQuestionKeys.NUMBER_OF_FULLTIME_EMPLOYEES,
    copy: { title: 'How many Full-Time employees do you have?' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        copy: 'Number of Full-Time employees',
        key: SMBCapitalCustomQuestionKeys.NUMBER_OF_FULLTIME_EMPLOYEES,
      },
    ],
    dynamicLogic: inList('hasEmployees.values', 'fullTimeEmployees'),
  },
  [SMBCapitalCustomQuestionKeys.OFFERS_BENEFITS_FULLTIME]: {
    key: SMBCapitalCustomQuestionKeys.OFFERS_BENEFITS_FULLTIME,
    copy: { title: 'Do you offer benefits to your Full-Time employees?' },
    fields: [
      {
        key: 'offersBenefitsFulltime.offersBenefitsFulltime',
        type: FieldType.RadioList,
        options: YesNoOptions,
      },
      {
        key: 'offersBenefitsFulltime.why.label',
        type: FieldType.Typography,
        props: { variant: 'body', alignment: 'center' },
        copy: 'If no, why?',
        dynamicLogic: eq('offersBenefitsFulltime.offersBenefitsFulltime', 'no'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'offersBenefitsFulltime.why',
        dynamicLogic: eq('offersBenefitsFulltime.offersBenefitsFulltime', 'no'),
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 200 },
        },
        copy: 'Reason',
      },
    ],
    dynamicLogic: inList('hasEmployees.values', 'fullTimeEmployees'),
  },
  [SMBCapitalCustomQuestionKeys.FULL_TIME_BENEFITS]: {
    key: SMBCapitalCustomQuestionKeys.FULL_TIME_BENEFITS,
    copy: {
      title: 'What benefits do you currently offer Full-Time employees?',
    },
    fields: [
      {
        key: 'fullTimeBenefits.benefits',
        type: FieldType.Dropdown,
        options: BENEFITS,
        copy: 'Full-Time Benefits',
        props: { multiple: true },
      },
      {
        key: 'fullTimeBenefits.other.label',
        type: FieldType.Typography,
        props: { variant: 'body', alignment: 'center' },
        copy: 'If you provide another benefit not included, please describe it here:',
        dynamicLogic: inList('fullTimeBenefits.benefits', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'fullTimeBenefits.other',
        dynamicLogic: inList('fullTimeBenefits.benefits', 'other'),
        copy: 'Other benefit',
      },
    ],
    dynamicLogic: and([
      eq('offersBenefitsFulltime.offersBenefitsFulltime', 'yes'),
      inList('hasEmployees.values', 'fullTimeEmployees'),
    ]),
  },
  [SMBCapitalCustomQuestionKeys.NUMBER_OF_PARTTIME_EMPLOYEES]: {
    key: SMBCapitalCustomQuestionKeys.NUMBER_OF_PARTTIME_EMPLOYEES,
    copy: { title: 'How many Part-Time employees do you have?' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        copy: 'Number of Part-Time employees',
        key: SMBCapitalCustomQuestionKeys.NUMBER_OF_PARTTIME_EMPLOYEES,
      },
    ],
    dynamicLogic: inList('hasEmployees.values', 'partTimeEmployees'),
  },
  [SMBCapitalCustomQuestionKeys.OFFERS_BENEFITS_PARTTIME]: {
    key: SMBCapitalCustomQuestionKeys.OFFERS_BENEFITS_PARTTIME,
    copy: { title: 'Do you offer benefits to your Part-Time employees?' },
    fields: [
      {
        key: SMBCapitalCustomQuestionKeys.OFFERS_BENEFITS_PARTTIME,
        type: FieldType.RadioList,
        options: YesNoOptions,
      },
    ],
    dynamicLogic: inList('hasEmployees.values', 'partTimeEmployees'),
  },
  [SMBCapitalCustomQuestionKeys.PART_TIME_BENEFITS]: {
    key: SMBCapitalCustomQuestionKeys.PART_TIME_BENEFITS,
    copy: {
      title: 'What benefits do you currently offer Part-Time employees?',
    },
    fields: [
      {
        key: 'partTimeBenefits.benefits',
        type: FieldType.Dropdown,
        options: BENEFITS,
        copy: 'Part-Time Benefits',
        props: { multiple: true },
      },
      {
        key: 'partTimeBenefits.other.label',
        type: FieldType.Typography,
        props: { variant: 'body', alignment: 'center' },
        copy: 'If you provide another benefit not included, please describe it here:',
        dynamicLogic: inList('partTimeBenefits.benefits', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'partTimeBenefits.other',
        dynamicLogic: inList('partTimeBenefits.benefits', 'other'),
        copy: 'Other benefit',
      },
    ],
    dynamicLogic: and([
      eq(SMBCapitalCustomQuestionKeys.OFFERS_BENEFITS_PARTTIME, 'yes'),
      inList('hasEmployees.values', 'partTimeEmployees'),
    ]),
  },
  [SMBCapitalCustomQuestionKeys.PLAN_TO_HIRE]: {
    key: SMBCapitalCustomQuestionKeys.PLAN_TO_HIRE,
    copy: { title: 'Do you plan to hire additional employees within the next year?' },
    fields: [
      {
        key: 'planToHire.planToHire',
        type: FieldType.CheckboxGroup,
        options: [
          {
            label: 'I plan to hire additional Full-Time employees',
            value: 'hireAdditionalFullTimeEmployees',
          },
          {
            label: 'I plan to hire additional Part-Time employees',
            value: 'hireAdditionalPartTimeEmployees',
          },
          { label: "I don't plan to hire any additional employees", value: 'noHiringPlans' },
        ],
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.PLANNED_FULLTIME_ROLES]: {
    key: SMBCapitalCustomQuestionKeys.PLANNED_FULLTIME_ROLES,
    copy: { title: 'How many Full-Time employees do you expect to add within the next year?' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        copy: 'Number of Full-Time employees',
        key: 'plannedFulltimeRoles.number',
      },
      {
        type: FieldType.Typography,
        props: { variant: 'body', alignment: 'center' },
        copy: 'What full-time role will you be hiring for?',
        key: 'plannedFulltimeRoles.roles.label',
      },
      {
        key: 'plannedFulltimeRoles.roles',
        type: FieldType.Dropdown,
        options: ROLES,
        copy: 'Full-Time roles',
        props: { multiple: true },
      },
      {
        key: 'plannedFulltimeRoles.other.label',
        type: FieldType.Typography,
        props: { variant: 'body', alignment: 'center' },
        copy: 'If you plan on adding another role not included, please describe it here:',
        dynamicLogic: inList('plannedFulltimeRoles.roles', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'plannedFulltimeRoles.other',
        dynamicLogic: inList('plannedFulltimeRoles.roles', 'other'),
        copy: 'Other role',
      },
    ],
    dynamicLogic: inList('planToHire.planToHire', 'hireAdditionalFullTimeEmployees'),
  },

  [SMBCapitalCustomQuestionKeys.PLANNED_PARTTIME_ROLES]: {
    key: SMBCapitalCustomQuestionKeys.PLANNED_PARTTIME_ROLES,
    copy: { title: 'How many Part-Time employees do you expect to add within the next year?' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        copy: 'Number of Part-Time employees',
        key: 'plannedParttimeRoles.number',
      },
      {
        type: FieldType.Typography,
        props: { variant: 'body', alignment: 'center' },
        copy: 'What part-time role will you be hiring for?',
        key: 'plannedParttimeRoles.roles.label',
      },
      {
        key: 'plannedParttimeRoles.roles',
        type: FieldType.Dropdown,
        options: ROLES,
        copy: 'Part-Time Roles',
        props: { multiple: true },
      },
      {
        key: 'plannedParttimeRoles.other.label',
        type: FieldType.Typography,
        props: { variant: 'body', alignment: 'center' },
        copy: 'If you plan on adding another role not included, please describe it here:',
        dynamicLogic: inList('plannedParttimeRoles.roles', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'plannedParttimeRoles.other',
        dynamicLogic: inList('plannedParttimeRoles.roles', 'other'),
        copy: 'Other role',
      },
    ],
    dynamicLogic: inList('planToHire.planToHire', 'hireAdditionalPartTimeEmployees'),
  },
  [SMBCapitalCustomQuestionKeys.SELL_TO_GOV_OR_CORP]: {
    key: SMBCapitalCustomQuestionKeys.SELL_TO_GOV_OR_CORP,
    copy: { title: 'Does your business sell to governments or corporations?' },
    fields: [
      {
        type: FieldType.CheckboxGroup,
        options: [
          { label: 'Yes, my business sells to governments', value: 'sellsToGovernments' },
          { label: 'Yes, my business sells to corporations', value: 'sellsToCorporations' },
          {
            label: 'No, my business does not sell to governments or corporations',
            value: 'no',
          },
        ],
        key: 'sellToGovOrCorp.sellToGovOrCorp',
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.CERTIFICATIONS]: {
    key: SMBCapitalCustomQuestionKeys.CERTIFICATIONS,
    copy: { title: 'Does your business have any of the following certifications?' },
    fields: [
      {
        key: 'certifications.certifications',
        type: FieldType.Dropdown,
        options: CERTIFICATIONS,
        copy: 'Certifications',
        props: { multiple: true },
      },
      {
        key: 'certifications.other.label',
        type: FieldType.Typography,
        props: { variant: 'body', alignment: 'center' },
        copy: 'If your certification is not included, please describe it here:',
        dynamicLogic: inList('certifications.certifications', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'certifications.other',
        dynamicLogic: inList('certifications.certifications', 'other'),
        copy: 'Other certification',
      },
    ],
    dynamicLogic: or([
      inList('sellToGovOrCorp.sellToGovOrCorp', 'sellsToGovernments'),
      inList('sellToGovOrCorp.sellToGovOrCorp', 'sellsToCorporations'),
    ]),
  },
  [SMBCapitalCustomQuestionKeys.HAS_GOV_CONTRACT]: {
    key: SMBCapitalCustomQuestionKeys.HAS_GOV_CONTRACT,
    copy: {
      title: 'Has your business ever been awarded a Government contract?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'hasGovContract.hasGovContract',
        options: YesNoOptions,
      },
      {
        type: FieldType.Typography,
        key: 'hasGovContract.totalRevenue.label',
        copy: 'Estimate the revenue generated from Government contracts for your business.',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: eq('hasGovContract.hasGovContract', 'yes'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Please Enter Whole Dollar Amount',
        key: 'hasGovContract.totalRevenue',
        dynamicLogic: eq('hasGovContract.hasGovContract', 'yes'),
      },
    ],
    dynamicLogic: or([
      inList('sellToGovOrCorp.sellToGovOrCorp', 'sellsToGovernments'),
      inList('sellToGovOrCorp.sellToGovOrCorp', 'sellsToCorporations'),
    ]),
  },
  [SMBCapitalCustomQuestionKeys.PART_OF_CORP_DIVERSITY_PROGRAM]: {
    key: SMBCapitalCustomQuestionKeys.PART_OF_CORP_DIVERSITY_PROGRAM,
    copy: { title: 'Does your business participate in a Corporate Supplier Diversity program?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.PART_OF_CORP_DIVERSITY_PROGRAM,
        options: YesNoOptions,
      },
    ],
    dynamicLogic: or([
      inList('sellToGovOrCorp.sellToGovOrCorp', 'sellsToGovernments'),
      inList('sellToGovOrCorp.sellToGovOrCorp', 'sellsToCorporations'),
    ]),
  },
  [SMBCapitalCustomQuestionKeys.HAS_CORP_CONTRACT]: {
    key: SMBCapitalCustomQuestionKeys.HAS_CORP_CONTRACT,
    copy: {
      title: 'Has your business ever been awarded a Corporate contract?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'hasCorpContract.hasCorpContract',
        options: YesNoOptions,
      },
      {
        type: FieldType.Typography,
        key: 'hasCorpContract.totalRevenue.label',
        copy: 'Estimate the revenue generated from Government contracts for your business.',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: eq('hasCorpContract.hasCorpContract', 'yes'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Please Enter Whole Dollar Amount',
        key: 'hasCorpContract.totalRevenue',
        dynamicLogic: eq('hasCorpContract.hasCorpContract', 'yes'),
      },
    ],
    dynamicLogic: or([
      inList('sellToGovOrCorp.sellToGovOrCorp', 'sellsToGovernments'),
      inList('sellToGovOrCorp.sellToGovOrCorp', 'sellsToCorporations'),
    ]),
  },
  [SMBCapitalCustomQuestionKeys.RUNS_BUSINESS_FROM_HOME]: {
    key: SMBCapitalCustomQuestionKeys.RUNS_BUSINESS_FROM_HOME,
    copy: {
      title: 'Do you primarily run your business from home?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.RUNS_BUSINESS_FROM_HOME,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.HAS_WEBSITE]: {
    key: SMBCapitalCustomQuestionKeys.HAS_WEBSITE,
    copy: {
      title: 'Does your business have a website?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'hasWebsite.hasWebsite',
        options: YesNoOptions,
      },
      {
        type: FieldType.Typography,
        key: 'hasWebsite.url.label',
        copy: 'What is the link to your business website?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: eq('hasWebsite.hasWebsite', 'yes'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Link to website',
        key: 'hasWebsite.url',
        dynamicLogic: eq('hasWebsite.hasWebsite', 'yes'),
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.HAS_HOME_INTERNET]: {
    key: SMBCapitalCustomQuestionKeys.HAS_HOME_INTERNET,
    copy: {
      title: 'Do you have home internet?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'hasHomeInternet.hasHomeInternet',
        options: YesNoOptions,
      },
      {
        type: FieldType.Typography,
        key: 'hasHomeInternet.provider.label',
        copy: 'What provider?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: eq('hasHomeInternet.hasHomeInternet', 'yes'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Internet provider',
        key: 'hasHomeInternet.provider',
        dynamicLogic: eq('hasHomeInternet.hasHomeInternet', 'yes'),
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.APPLIED_TO_ACP]: {
    key: SMBCapitalCustomQuestionKeys.APPLIED_TO_ACP,
    copy: {
      title: 'Have you recently applied for the Affordable Connectivity Program (ACP)?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.APPLIED_TO_ACP,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.HAS_COMPUTER]: {
    key: SMBCapitalCustomQuestionKeys.HAS_COMPUTER,
    copy: {
      title: 'Do you have a personal or home computer?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.HAS_COMPUTER,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.DEVICES]: {
    key: SMBCapitalCustomQuestionKeys.DEVICES,
    copy: {
      title: 'Which device do you primarily use to run your business?',
    },
    fields: [
      {
        type: FieldType.Dropdown,
        key: SMBCapitalCustomQuestionKeys.DEVICES,
        options: DEVICES,
        copy: 'Devices',
        props: { multiple: true, selectAll: true },
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.TECH_INTERESTS]: {
    key: SMBCapitalCustomQuestionKeys.TECH_INTERESTS,
    copy: {
      title: 'What technology are you most interested in?',
    },
    fields: [
      {
        type: FieldType.Dropdown,
        key: SMBCapitalCustomQuestionKeys.TECH_INTERESTS,
        options: TECH_TOPICS,
        copy: 'Tech Interests',
        props: { multiple: true, selectAll: true },
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.PUBLIC_ASSISTANCE]: {
    key: SMBCapitalCustomQuestionKeys.PUBLIC_ASSISTANCE,
    copy: {
      title: 'Do you receive any form of public assistance?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: SMBCapitalCustomQuestionKeys.PUBLIC_ASSISTANCE,
        options: YesNoOptions,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.FEEDBACK]: {
    key: SMBCapitalCustomQuestionKeys.FEEDBACK,
    copy: {
      title:
        "Is there anything else you'd like us to know about your business or any other general feedback?",
    },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Feedback (optional)',
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 200 },
        },
        validation: { required: false },
        key: SMBCapitalCustomQuestionKeys.FEEDBACK,
      },
    ],
  },
  [SMBCapitalCustomQuestionKeys.SOURCE]: {
    key: SMBCapitalCustomQuestionKeys.SOURCE,
    copy: {
      title: 'How did you hear about this program?',
    },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Source',
        key: SMBCapitalCustomQuestionKeys.SOURCE,
      },
    ],
  },
  [CommonQuestionKeys.AFFIRMATION]: {
    key: CommonQuestionKeys.AFFIRMATION,
    copy: { title: 'Please confirm this statement:' },
    fields: [
      {
        type: FieldType.Checkbox,
        key: CommonQuestionKeys.AFFIRMATION,
        copy: 'I attest that the information I provided in this application is accurate to the best of my knowledge.',
      },
    ],
  },
};
